import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { Box, Button, Checkbox, Typography } from "@mui/material";
import { EventBusy } from "@mui/icons-material";
import { enUS, GridColDef, GridRenderCellParams, GridSortModel, ruRU } from "@mui/x-data-grid-premium";

import { EnabledLanguages } from "@framework/constants";
import { EventsTrainSortColumn } from "@framework/types";
import { ProgressOverlay } from "@framework/mui-page-layout";
import { useApiCall } from "@framework/react-hooks";
import { ConfirmationDialog } from "@framework/mui-dialog-confirmation";
import { SortDirection } from "@framework/types-collection";

import { EmptyPlaceholder } from "../../../../../../components/common/empty-placeholder";
import { downloadCsv } from "../../../../../../utils/download-csv";
import { SOUND_ALARM_REFRESH_EVENT } from "../../../../../../components/common/header/sound-alarm/constants";
import { useEventType } from "../../../../../../utils/event";
import { sortMatchForDb, sortMatchForTable } from "./utils";
import { SemaforIcon } from "../../../../../../utils";
import { useFormatDateHumanReadable, useToFakeUtcISO } from "../../../../../../hooks";
import { RangeType, toFakeUtc, useAsu } from "@framework/provider-asu";
import { IResponseEventsLog, IEventsPageResponse, IRequestEventsLog } from "../events-train/interfaces";
import { mapEventsToRows } from "../events-train/mappers";
import { StyledDataGridPremium } from "./styled";
import { ExportToolbar } from "./components";

const TAKE_NORMAL = 50;
const TAKE_RECORDER = 100;

export const TrainEvents: FC = () => {
  const {
    selectedItem,
    startTimestamp,
    endTimestamp,
    dbTz,
    userSelectedRangeType,
    recorderTickId,
    recorderWindowStart,
    userSelectedRecorderModePeriod,
    recorderModeInterval,
  } = useAsu();

  const [rawEvents, setRawEvents] = useState<IResponseEventsLog[]>([]);
  const [loadServiceEvents, setLoadServiceEvents] = useState<boolean>(false);
  const [sortColumn, setSortColumn] = useState<EventsTrainSortColumn>(EventsTrainSortColumn.EventLogDate);
  const [sortOrder, setSortOrder] = useState<SortDirection>(SortDirection.desc);
  const [skip, setSkip] = useState<number>(0);

  const trainId = selectedItem && "TrainId" in selectedItem ? selectedItem.TrainId : null;
  const isRecorderMode = userSelectedRangeType === RangeType.RECORDER_MODE;
  const take = isRecorderMode ? TAKE_RECORDER : TAKE_NORMAL;

  const [ackDialogOpen, setAckDialogOpen] = useState(false);
  const [ackEventId, setAckEventId] = useState<number | null>(null);
  const [isLastPage, setIsLastPage] = useState<boolean>(false);

  const rawEventsLengthRef = useRef(0);
  rawEventsLengthRef.current = rawEvents.length;

  const recorderFetchGenRef = useRef(0);

  const { formatMessage, locale } = useIntl();
  const formatDate = useFormatDateHumanReadable();
  const toFakeUtcISO = useToFakeUtcISO();

  const noEvents = formatMessage({ id: "pages.scheme.events.noEvents" });
  const date = toFakeUtcISO(new Date()).split("T")[0];
  const exportFileTitle =
    (selectedItem && "TrainName" in selectedItem && selectedItem.TrainName) ||
    formatMessage({ id: "pages.eventsLog.title" });
  const exportFileName = `train-events-report-${exportFileTitle}-${date}`;

  const { getEventType, getMessage } = useEventType();

  const recorderWindowStartRef = useRef(recorderWindowStart);
  recorderWindowStartRef.current = recorderWindowStart;

  const endTimestampRef = useRef(endTimestamp);
  endTimestampRef.current = endTimestamp;

  const dbTzRef = useRef(dbTz);
  dbTzRef.current = dbTz;

  // refs justLeftRecorderModeRef и prevIsRecorderModeRef для того, чтобы не запускать эффект при первом рендере после выхода из recorder mode.
  const justLeftRecorderModeRef = useRef(false);
  const prevIsRecorderModeRef = useRef(isRecorderMode);

  if (prevIsRecorderModeRef.current !== isRecorderMode) {
    prevIsRecorderModeRef.current = isRecorderMode;
    if (!isRecorderMode) {
      justLeftRecorderModeRef.current = true;
    }
  }

  const recorderTrimLeftMs = (): number | null => {
    const wStr = recorderWindowStartRef.current;
    if (!wStr) return null;
    const w = new Date(wStr).getTime();
    return Number.isNaN(w) ? null : w;
  };

  const trimRawEventsToWindow = useCallback((rows: IResponseEventsLog[]): IResponseEventsLog[] => {
    const leftMs = recorderTrimLeftMs();
    if (leftMs === null) return rows;
    const endMs = new Date(endTimestampRef.current).getTime();
    if (!Number.isNaN(endMs) && leftMs >= endMs) {
      return rows;
    }
    const tz = dbTzRef.current;
    return rows.filter(e => {
      const t = toFakeUtc(e.EventLogDate, tz).getTime();
      return !Number.isNaN(t) && t >= leftMs;
    });
  }, []);

  const mergeUniqueEvents = (prev: IResponseEventsLog[], next: IResponseEventsLog[]): IResponseEventsLog[] => {
    const existingIds = new Set(prev.map(e => e.idEvent));
    const newItems = next.filter(item => !existingIds.has(item.idEvent));
    return [...prev, ...newItems];
  };

  const recorderRawForDisplay = useMemo((): IResponseEventsLog[] => {
    if (!isRecorderMode) return [];
    if (recorderTickId === 1) return rawEvents;
    return trimRawEventsToWindow(rawEvents);
  }, [isRecorderMode, rawEvents, recorderTickId]);

  const tableRows = useMemo(
    () =>
      mapEventsToRows(isRecorderMode ? recorderRawForDisplay : rawEvents, {
        formatDate,
        formatMessage,
        getEventType,
        getMessage,
      }),
    [isRecorderMode, recorderRawForDisplay, rawEvents, formatDate, formatMessage, getEventType, getMessage],
  );

  const { fn: getEventLog, isLoading: isGettingEventLog } = useApiCall<IEventsPageResponse>(
    (api, { id, loadServiceEvents, skip, sortOrder, sortColumn, take, startOverride }: IRequestEventsLog) => {
      const requestSortColumn = isRecorderMode ? EventsTrainSortColumn.EventLogDate : sortColumn;
      const requestSortOrder = isRecorderMode ? "desc" : sortOrder;
      const data = {
        startTimestamp: startOverride ?? startTimestamp,
        endTimestamp,
        idTrain: id,
        loadServiceEvents,
        skip,
        take,
        ...(requestSortColumn ? { sortColumn: requestSortColumn, sortOrder: requestSortOrder } : {}),
      };

      // for Station events we use: services/ui/src/pages/dashboard/tabs/events-log/table.tsx
      return api.fetchJson({
        url: `/events/train`,
        data,
      }) as Promise<IEventsPageResponse>;
    },
    { success: false },
  );

  const { fn: setAckStatus, isLoading: isAcknowledging } = useApiCall(
    (api, values: { ackEventId: number }) => {
      const { ackEventId } = values;
      return (
        api.fetchJson({
          url: `/events/ack-event/${ackEventId}`,
          method: "PUT",
        }) as Promise<unknown>
      )
        .then(() => {
          window.dispatchEvent(new Event(SOUND_ALARM_REFRESH_EVENT));

          return getEventLog(undefined, {
            id: trainId,
            loadServiceEvents,
            skip,
            take,
          });
        })
        .then((json: unknown) => {
          const page = json as IEventsPageResponse | null | undefined;
          const rows = page?.items;
          if (!rows?.length) return;
          setRawEvents(rows);
        })
        .catch(error => {
          console.error("Error acknowledging event:", error);
          throw error;
        })
        .finally(() => {
          setAckDialogOpen(false);
          setAckEventId(null);
        });
    },
    { success: false },
  );

  const handleOnSortModelChange = (sortModel: GridSortModel) => {
    if (!sortModel.length) {
      return;
    }

    setSkip(0);
    setSortColumn(sortMatchForDb[sortModel[0].field as EventsTrainSortColumn]);
    setSortOrder((sortModel[0].sort === "asc" ? SortDirection.asc : SortDirection.desc) || SortDirection.desc);
  };

  const localeText =
    (locale as EnabledLanguages) === EnabledLanguages.RU
      ? ruRU.components.MuiDataGrid.defaultProps.localeText
      : enUS.components.MuiDataGrid.defaultProps.localeText;

  const ellipsisCell = (value: GridRenderCellParams["value"]) => {
    const text = String(value ?? "");

    return (
      <div
        title={text}
        style={{
          width: "100%",
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }}
      >
        {text}
      </div>
    );
  };
  const rawEventsMap = useMemo(() => new Map(rawEvents.map(e => [e.idEvent, e])), [rawEvents]);
  const columns: GridColDef[] = [
    {
      field: "ackStatus",
      headerName: formatMessage({ id: "pages.eventsLog.messages.headers.ackStatus" }),
      width: 50,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const rawEvent = rawEventsMap.get(params.row.idEvent);
        const flags = rawEvent?.Flags ?? 0;
        const isChecked = flags === 1 || flags === 2;
        const isDisabled = flags !== 0;

        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
            <Checkbox
              checked={isChecked}
              disabled={isDisabled}
              onChange={() => {
                if (!isDisabled) {
                  setAckEventId(rawEvent?.idEvent ?? null);
                  setAckDialogOpen(true);
                }
              }}
              size="small"
              sx={{
                padding: 0,
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "status",
      description: formatMessage({ id: "pages.eventsLog.messages.headers.status" }),
      headerName: formatMessage({ id: "pages.eventsLog.messages.headers.status" }),
      width: 50,
      sortable: !isRecorderMode,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
            <SemaforIcon status={Number(params.value) || 0} width={12} height={12} />
          </Box>
        );
      },
    },
    {
      field: "type",
      description: formatMessage({ id: "pages.eventsLog.messages.headers.eventType" }),
      headerName: formatMessage({ id: "pages.eventsLog.messages.headers.eventType" }),
      width: 120,
      sortable: !isRecorderMode,
      renderCell: params => ellipsisCell(params.value),
    },
    {
      field: "pointName",
      headerName: formatMessage({ id: "pages.eventsLog.messages.headers.point" }),
      width: 120,
      sortable: !isRecorderMode,
      renderCell: params => ellipsisCell(params.value),
    },
    {
      field: "date",
      headerName: formatMessage({ id: "pages.eventsLog.messages.headers.dateTime" }),
      width: 150,
      sortable: !isRecorderMode,
      renderCell: params => ellipsisCell(params.value),
    },
    {
      field: "parameter",
      headerName: formatMessage({ id: "pages.eventsLog.messages.headers.parameter" }),
      width: 200,
      sortable: !isRecorderMode,
      renderCell: params => ellipsisCell(params.value),
    },
    {
      field: "source",
      description: formatMessage({ id: "pages.eventsLog.messages.headers.source" }),
      headerName: formatMessage({ id: "pages.eventsLog.messages.headers.source" }),
      width: 100,
      sortable: !isRecorderMode,
      renderCell: params => ellipsisCell(params.value),
    },
    {
      field: "fullName",
      description: formatMessage({ id: "pages.eventsLog.messages.headers.fullName" }),
      headerName: formatMessage({ id: "pages.eventsLog.messages.headers.fullName" }),
      width: 150,
      sortable: !isRecorderMode,
      renderCell: params => ellipsisCell(params.value),
    },
    {
      field: "message",
      headerName: formatMessage({ id: "pages.eventsLog.messages.headers.message" }),
      width: 400,
      sortable: !isRecorderMode,
      renderCell: params => ellipsisCell(params.value),
    },
    {
      field: "defect",
      description: formatMessage({ id: "pages.eventsLog.messages.headers.defect" }),
      headerName: formatMessage({ id: "pages.eventsLog.messages.headers.defect" }),
      width: 150,
      sortable: !isRecorderMode,
      renderCell: params => ellipsisCell(params.value),
    },
  ];

  const loadMore = () => setSkip(prev => prev + take);

  useEffect(() => {
    if (!isRecorderMode) return;

    setRawEvents([]);
    setSkip(0);
    setIsLastPage(false);
  }, [trainId, isRecorderMode, userSelectedRecorderModePeriod, recorderModeInterval]);

  useEffect(() => {
    if (!trainId || !isRecorderMode || !recorderTickId) return;

    const fetchGen = ++recorderFetchGenRef.current;

    const useFullRecorderWindow = recorderTickId === 1 || rawEventsLengthRef.current === 0;

    const startOverride = useFullRecorderWindow ? recorderWindowStart ?? startTimestamp : undefined;

    void getEventLog(undefined, {
      id: trainId,
      loadServiceEvents,
      skip: 0,
      take,
      ...(startOverride !== undefined ? { startOverride } : {}),
    }).then((json: unknown) => {
      if (fetchGen !== recorderFetchGenRef.current) return;
      const page = json as IEventsPageResponse | null | undefined;
      const rows = page?.items ?? [];

      if (rows.length === 0) {
        if (recorderTickId === 1) {
          setRawEvents([]);
        }
        return;
      }

      if (recorderTickId === 1) {
        setRawEvents(rows);
      } else {
        setRawEvents(prev => mergeUniqueEvents(prev, rows));
      }
    });
  }, [trainId, loadServiceEvents, isRecorderMode, recorderTickId, recorderWindowStart, startTimestamp, endTimestamp]);

  useEffect(() => {
    if (isRecorderMode || !trainId || !skip) return;

    void getEventLog(undefined, {
      id: trainId,
      loadServiceEvents,
      skip,
      take,
      sortColumn,
      sortOrder,
    }).then((json: unknown) => {
      const page = json as IEventsPageResponse | null | undefined;
      const rows = page?.items;

      if (!rows?.length) {
        setIsLastPage(true);
        return;
      }

      setRawEvents(prev => [...prev, ...rows]);

      setIsLastPage((page?.items?.length ?? 0) < take || (page?.page ?? 0) >= (page?.pagesCount ?? 0));
    });
  }, [skip, isRecorderMode]);

  useEffect(() => {
    if (!trainId || isRecorderMode) return;

    if (justLeftRecorderModeRef.current) {
      justLeftRecorderModeRef.current = false;
      return;
    }

    setRawEvents([]);
    setIsLastPage(false);
    setSkip(0);

    void getEventLog(undefined, {
      id: trainId,
      loadServiceEvents,
      skip: 0,
      take,
      sortColumn,
      sortOrder,
    }).then((json: unknown) => {
      const page = json as IEventsPageResponse | null | undefined;
      const rows = page?.items;

      if (!rows?.length) return;

      setRawEvents(rows);
      setIsLastPage((page?.items?.length ?? 0) < take || (page?.page ?? 0) >= (page?.pagesCount ?? 0));
    });
  }, [trainId, startTimestamp, endTimestamp, loadServiceEvents, isRecorderMode, sortColumn, sortOrder]);

  const loading = isGettingEventLog || isAcknowledging;

  const eventsLogId = "pages.eventsLog";

  const handleExport = useCallback((): void => {
    if (!tableRows.length) {
      return;
    }

    downloadCsv(
      tableRows.map(row => ({
        ackStatus: row.ackStatus != null ? formatMessage({ id: `enums.ackStatus.${row.ackStatus}` }) : "",
        status: row.status ?? "",
        type: row.type ?? "",
        pointName: row.pointName ?? "",
        date: row.date ?? "",
        parameter: row.parameter ?? "",
        source: row.source ?? "",
        fullName: row.fullName ?? "",
        message: row.message ?? "",
        defect: row.defect ?? "",
      })),
      `${exportFileName}.csv`,
      [
        { field: "ackStatus", title: formatMessage({ id: `${eventsLogId}.messages.headers.ackStatus` }) },
        { field: "status", title: formatMessage({ id: `${eventsLogId}.messages.headers.status` }) },
        { field: "type", title: formatMessage({ id: `${eventsLogId}.messages.headers.eventType` }) },
        { field: "pointName", title: formatMessage({ id: `${eventsLogId}.messages.headers.point` }) },
        { field: "date", title: formatMessage({ id: `${eventsLogId}.messages.headers.dateTime` }) },
        { field: "parameter", title: formatMessage({ id: `${eventsLogId}.messages.headers.parameter` }) },
        { field: "source", title: formatMessage({ id: `${eventsLogId}.messages.headers.source` }) },
        { field: "fullName", title: formatMessage({ id: `${eventsLogId}.messages.headers.fullName` }) },
        { field: "message", title: formatMessage({ id: `${eventsLogId}.messages.headers.message` }) },
        { field: "defect", title: formatMessage({ id: `${eventsLogId}.messages.headers.defect` }) },
      ],
    );
  }, [tableRows, exportFileName, formatMessage]);

  const Toolbar = useCallback(
    () => (
      <ExportToolbar
        value={loadServiceEvents}
        label={formatMessage({ id: "pages.eventsLog.serviceEvents" })}
        onChange={setLoadServiceEvents}
        handleExport={handleExport}
      />
    ),
    [loadServiceEvents, formatMessage, handleExport],
  );

  const gridComponents = useMemo(() => ({ Toolbar }), [Toolbar]);

  return (
    <>
      <ConfirmationDialog
        open={ackDialogOpen}
        onCancel={() => setAckDialogOpen(false)}
        onConfirm={() => {
          if (ackEventId) {
            void setAckStatus(undefined, { ackEventId });
          }
        }}
        maxWidth="xs"
        disabled={isAcknowledging}
        message="pages.eventsLog.ack.confirmTitle"
      >
        <ProgressOverlay isLoading={isAcknowledging}>
          <FormattedMessage id="pages.eventsLog.ack.confirmText" />
        </ProgressOverlay>
      </ConfirmationDialog>
      <ProgressOverlay
        isLoading={loading}
        wrapperSx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        {tableRows.length ? (
          <>
            <StyledDataGridPremium
              sx={{
                flex: 1,
                minHeight: "137px",
                pointerEvents: loading ? "none" : "all",
                ".MuiDataGrid-iconButtonContainer": {
                  visibility: "visible",
                },
                ".MuiDataGrid-sortIcon": {
                  opacity: "inherit !important",
                },
                "& .MuiDataGrid-virtualScroller": {
                  overflowX: "auto !important",
                },
              }}
              components={gridComponents}
              initialState={{
                sorting: {
                  sortModel: [
                    {
                      field: sortMatchForTable[sortColumn],
                      sort: sortOrder || "desc",
                    },
                  ],
                },
              }}
              sortingMode="server"
              onSortModelChange={isRecorderMode ? undefined : handleOnSortModelChange}
              localeText={localeText}
              columnBuffer={columns.length}
              rowBuffer={50}
              rowHeight={57}
              headerHeight={57}
              columns={columns}
              rows={tableRows}
              hideFooter
              disableColumnFilter
              disableColumnMenu
              disableColumnReorder
              disableMultipleColumnsSorting
              disableSelectionOnClick
              showColumnRightBorder
              showCellRightBorder
            />
            {!isRecorderMode && tableRows.length > 0 && (
              <Box sx={{ p: 1, display: "flex", justifyContent: "center" }}>
                {!isLastPage ? (
                  <Button variant="outlined" fullWidth onClick={loadMore} disabled={loading}>
                    <FormattedMessage id="pages.journals.buttons.loadMore" />
                  </Button>
                ) : (
                  <Typography variant="caption">
                    <FormattedMessage id="pages.journals.captions.noRecords" />
                  </Typography>
                )}
              </Box>
            )}
          </>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", p: "2px 8px" }}>
              <Checkbox checked={loadServiceEvents} onChange={e => setLoadServiceEvents(e.currentTarget.checked)} />
              <Typography sx={{ marginRight: "1rem" }}>
                {formatMessage({ id: "pages.eventsLog.serviceEvents" })}
              </Typography>
            </Box>
            <EmptyPlaceholder icon={<EventBusy />} text={noEvents} />
          </>
        )}
      </ProgressOverlay>
    </>
  );
};
